// Shared pieces of the post-signing payment stage, ported from the Lovable reference
// (components/ownership/OwnershipParts.tsx, components/dealflow/DealWaitingNotice.tsx) into
// KlargoReact's design system. Same hierarchy and tokens; no behaviour.
import React from "react";
import RegistrationPlate from "../../common/RegistrationPlate";
import { StatusDot } from "../StatusMark";
import { formatPaymentAmount } from "./swishApp2App";

export const H1_CLASS = "m-0 text-[clamp(24px,2.6vw,30px)] leading-[1.12]";
export const LEAD_CLASS = "mb-0 mt-3 max-w-[58ch] text-[15px] leading-[1.55] text-ds-text";
export const META_CLASS = "mb-0 max-w-[58ch] text-[13.5px] leading-[1.55] text-ds-muted";

/** The one primary action of a moment: ink, full width on a phone. */
export const PRIMARY_ACTION = "ds-action-ink w-full justify-center sm:w-auto sm:min-w-[220px] disabled:opacity-60";
/** A real second way, never the equal of the primary. */
export const OUTLINE_ACTION = "ds-action-outline justify-center disabled:opacity-60";
export const ACTION_ROW = "mt-7 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-4";

/** The deal's identity band: plate and the signed price — the one place the amount is stated. */
export function DealIdentityBand({ plate, amount, language, t }) {
  return (
    <div className="mb-8 flex flex-wrap items-center justify-between gap-x-8 gap-y-3 border-t-2 border-ds-ink pt-4">
      {plate ? <RegistrationPlate value={plate} size="row" /> : <span />}
      <div className="text-right">
        <div className="text-[12.5px] font-medium text-ds-muted">{t("payment.band.price")}</div>
        <div className="mt-0.5 text-[17px] font-semibold tabular-nums text-ds-ink" data-testid="payment-amount">
          {formatPaymentAmount(amount, language)}
        </div>
      </div>
    </div>
  );
}

/** "Betalning" with its state mark: slate while open, green only once the seller confirmed. */
export function StageLabel({ confirmed, t }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <StatusDot tone={confirmed ? "green" : "slate"} />
      <span className="text-[13px] font-semibold tracking-[0.02em] text-ds-muted">{t("payment.stage")}</span>
    </div>
  );
}

/**
 * Calm named waiting state: one subtle animation, who is awaited, the reassurance line.
 * Waiting is slate — neutral patience, never celebration or warning (§7).
 *
 * With a `title` the party awaited gets their own line and `text` becomes the reason under it,
 * as the reference has it on the Ägarbyte stage. Without one, `text` carries the weight — which
 * is how the payment stage uses it.
 */
export function DealWaitingNotice({ title, text, t }) {
  return (
    <div role="status" aria-live="polite" className="rounded-[10px] border border-[rgba(20,36,46,.08)] bg-ds-slate-bg px-4 py-4 sm:px-5">
      <div className="flex items-start gap-3">
        <span className="mt-1.5 h-2 w-2 shrink-0 animate-pulse rounded-full bg-ds-slate" aria-hidden="true" />
        <div>
          {title && <p className="m-0 text-sm font-semibold text-ds-ink">{title}</p>}
          <p className={`m-0 text-sm ${title ? "mt-0.5 text-ds-text" : "font-semibold text-ds-ink"}`}>{text}</p>
        </div>
      </div>
      <p className="m-0 mt-1.5 pl-5 text-[12.5px] text-ds-muted">{t("payment.waitingHint")}</p>
    </div>
  );
}

/** Hairline fact rows — who receives the money, how much, what Swish shows. No panel. */
export function FactRows({ rows }) {
  return (
    <div className="border-t border-ds-rule-row">
      {rows.map((row) => (
        <div key={row.key} className="flex items-baseline justify-between gap-6 border-b border-ds-rule-row py-3" data-testid={row.testId}>
          <span className="ds-label">{row.label}</span>
          <span className={`text-right text-[15px] font-semibold text-ds-ink${row.nowrap ? " whitespace-nowrap" : ""}`}>
            {row.value}
            {row.meta && <span className="ds-meta block tabular-nums sm:ml-2 sm:inline">{row.meta}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

/** A quiet informational line under the action (Swish result, changes, hints). */
export function Notice({ tone = "slate", children, testId }) {
  const border = tone === "red" ? "border-[color:var(--ds-red)]" : "border-[rgba(20,36,46,.18)]";
  return (
    <div role="status" aria-live="polite" className={`mt-5 border-l-2 ${border} pl-3`} data-testid={testId}>
      <p className={`${META_CLASS} text-ds-text`}>{children}</p>
    </div>
  );
}

/**
 * The shape of what is coming, while the stage does not yet know where the deal stands (the
 * reference's FlowStagePlaceholder): a quiet skeleton at the stage's own measure, with the name of
 * the stage being prepared announced to assistive technology. Used by the page while the deal
 * loads and by the stage while its state loads, so there is never a blank frame in between.
 */
export function StagePlaceholder({ label }) {
  return (
    <div className="mx-auto w-full" style={{ maxWidth: "42rem" }} role="status" aria-busy="true" data-testid="payment-preparing">
      <span className="sr-only">{label}</span>
      <div className="border-t-2 border-ds-ink pt-4" aria-hidden="true">
        <div className="h-6 w-28 animate-pulse rounded bg-[rgba(20,36,46,.08)]" />
      </div>
      <div className="mt-8 h-7 w-2/3 animate-pulse rounded bg-[rgba(20,36,46,.08)]" aria-hidden="true" />
      <div className="mt-4 h-4 w-1/2 animate-pulse rounded bg-[rgba(20,36,46,.08)]" aria-hidden="true" />
    </div>
  );
}

export const firstName = (name) => (name || "").trim().split(/\s+/)[0] || "";
